import { SignatureV4 } from '@smithy/signature-v4';
import { HttpRequest } from '@smithy/protocol-http';
import { Sha256 } from '@aws-crypto/sha256-browser';

/**
 * AWS Signature Version 4 for API Gateway (`execute-api`) on Web Crypto.
 *
 * This is the only module that knows about SigV4; every signed call (Apollo
 * HTTP link, WebSocket handshake) goes through it. The
 * signer is AWS's own (`@smithy/signature-v4`) hashing with `crypto.subtle`
 * (`@aws-crypto/sha256-browser`, pure-JS fallback where it is unavailable),
 * so the browser build needs no Node `crypto` polyfill. Both entry points are
 * async because `crypto.subtle` is.
 */

export type SigV4Credentials = {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
};

export type SignableRequest = {
  /** Absolute URL; path and query are taken from it. */
  url: string;
  method: string;
  /** Without `host` / `x-amz-*`; the signer adds those. */
  headers: Record<string, string>;
  body?: string;
};

const SERVICE = 'execute-api';

const makeSigner = (credentials: SigV4Credentials, region: string) =>
  new SignatureV4({
    credentials,
    region,
    service: SERVICE,
    sha256: Sha256,
    // Deliberate: `true` would add an `x-amz-content-sha256` request header,
    // which changes the CORS preflight API Gateway currently accepts. This
    // keeps the header set identical to what aws4 sent (host, x-amz-date,
    // x-amz-security-token, accept, content-type, authorization).
    applyChecksum: false,
  });

const hasHeader = (headers: Record<string, string>, name: string) =>
  Object.keys(headers).some((key) => key.toLowerCase() === name);

const utf8ByteLength = (value: string) =>
  new TextEncoder().encode(value).length;

const toHttpRequest = ({ url, method, headers, body }: SignableRequest) => {
  const parsed = new URL(url);
  const query: Record<string, string> = {};
  parsed.searchParams.forEach((value, key) => {
    query[key] = value;
  });
  // `aws4` signed `content-length` whenever the request had a body (the
  // browser sets that header itself with the same value); keep it in the
  // signed set so the signature stays byte-identical to the previous one.
  const contentLength: Record<string, string> =
    body && !hasHeader(headers, 'content-length')
      ? { 'content-length': String(utf8ByteLength(body)) }
      : {};
  return new HttpRequest({
    protocol: parsed.protocol,
    hostname: parsed.hostname,
    port: parsed.port ? Number(parsed.port) : undefined,
    path: parsed.pathname,
    query,
    method,
    // The SDK's HTTP handler adds `host`; the signer does not, and it must be
    // in the signed set.
    headers: { ...headers, ...contentLength, host: parsed.host },
    body,
  });
};

/**
 * RFC 3986 escaping (`encodeURIComponent` plus `!'()*`): the same encoding the
 * signer used for the canonical query, so the URL on the wire matches the
 * bytes that were signed.
 */
const escapeRfc3986 = (value: string) =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`
  );

const formatQuery = (query: Record<string, string | string[] | null>) =>
  Object.entries(query)
    .flatMap(([key, value]) => {
      if (value === null) return [];
      const values = Array.isArray(value) ? value : [value];
      return values.map((v) => `${escapeRfc3986(key)}=${escapeRfc3986(v)}`);
    })
    .join('&');

/**
 * Signs an HTTP request: returns its headers plus `host`, `x-amz-date`,
 * `x-amz-security-token` (when the credentials carry one) and
 * `authorization`. `signingDate` must already include any clock-drift
 * correction.
 */
export const signSigV4Headers = async (
  request: SignableRequest,
  credentials: SigV4Credentials,
  region: string,
  signingDate: Date
): Promise<Record<string, string>> => {
  const signed = await makeSigner(credentials, region).sign(
    toHttpRequest(request),
    { signingDate }
  );
  return signed.headers;
};

/**
 * Returns `url` with the SigV4 signature in its query string
 * (`X-Amz-Algorithm`, `X-Amz-Credential`, `X-Amz-Date`, `X-Amz-Expires`,
 * `X-Amz-SignedHeaders`, `X-Amz-Security-Token`, `X-Amz-Signature`), for the
 * WebSocket handshake where headers cannot be set.
 */
export const presignSigV4Url = async (
  url: string,
  credentials: SigV4Credentials,
  region: string,
  signingDate: Date,
  expiresIn = 3600
): Promise<string> => {
  const request = toHttpRequest({ url, method: 'GET', headers: {} });
  const signed = await makeSigner(credentials, region).presign(request, {
    signingDate,
    expiresIn,
  });
  const search = formatQuery(signed.query ?? {});
  return `${request.protocol}//${request.headers.host}${signed.path}${
    search ? `?${search}` : ''
  }`;
};
